import { Link as RouterLink } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { ContentLayout, HeaderBreadcrumbs, Loader } from "../../../shared";
import {
  FtiAmountBreakdown,
  FtiBilingualText,
  FtiDetailsCard,
  FtiDetailsField,
  FtiFormDialog,
  FtiOutputDialog,
  FtiPaymentMedia,
  FtiReprintDialog,
  FtiReprintHistory,
} from "../components";
import { useFullTaxInvoiceDetails } from "../hooks";
import { FTI_STATUS_STYLES } from "../constants";

const getGridSx = (columns) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, 1fr)` },
  gap: "24px",
  p: "24px",
});

export const FullTaxInvoiceDetails = () => {
  const {
    state,
    isStoreAdmin,
    isEditable,
    breadcrumbs,
    reissueInitialValues,
    reprintRemarks,
    formatAmount,
    exportingAction,
    isGenerating,
    handlePrint,
    handleConfirmReprint,
    handleOpenReprint,
    handleCloseReprint,
    handleOpenReissue,
    handleCloseReissue,
    handleReissueFti,
    handleFtiReissued,
  } = useFullTaxInvoiceDetails();
  const { details } = state;
  // A cancelled FTI can't be printed or re-issued any more
  const isIssued = details?.status === "issued";
  const address = [details?.full_business_address, details?.business_state]
    .filter(Boolean)
    .join(", ");

  return (
    <ContentLayout
      title={details?.fti_invoice_number ?? ""}
      chipType="other"
      statusContent={
        details?.status && (
          <Chip
            size="small"
            label={details.status}
            sx={{
              textTransform: "capitalize",
              ...FTI_STATUS_STYLES[details.status],
            }}
          />
        )
      }
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbs} />}
      rightContent={
        isIssued && (
          <Stack direction="row" alignItems="center" gap="16px">
            <Tooltip title="Print">
              <span>
                <IconButton
                  onClick={handleOpenReprint}
                  sx={{
                    color: "#1560D4",
                    bgcolor: "#E8F0FA",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#D6E4F7" },
                  }}
                >
                  <PrintOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            {isEditable && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleOpenReissue}
              >
                Cancel & Re-Issue/ยกเลิกและออกใหม่
              </Button>
            )}
          </Stack>
        )
      }
    >
      {state.isLoading ? (
        <Stack
          sx={{
            height: "calc(100vh - 200px)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </Stack>
      ) : (
        details && (
          <Stack gap="16px">
            <FtiDetailsCard>
              <Box sx={getGridSx(3)}>
                <FtiDetailsField label="Date/วันที่">
                  {/* The API sends the store's local time with a "Z", so it's read as UTC to keep that time */}
                  {details.issue_date_tz && (
                    <FtiBilingualText
                      en={moment
                        .utc(details.issue_date_tz)
                        .format("DD MMM YYYY h:mm A")}
                      th={details.display_issue_date}
                    />
                  )}
                </FtiDetailsField>
                <FtiDetailsField label="POS ID/รหัสเครื่อง">
                  {details.pos_code}
                </FtiDetailsField>
                <FtiDetailsField label="Replaces ABB Invoice No./ออกแทนใบกำกับภาษีอย่างย่อเลขที่">
                  {details.order_number && (
                    <Link
                      component={RouterLink}
                      to={`${isStoreAdmin ? "/store-admin" : ""}/orders/details/${
                        details.order_id
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                    >
                      {details.order_number}
                    </Link>
                  )}
                </FtiDetailsField>
              </Box>
            </FtiDetailsCard>

            {/* `rcipet_history` is the reprint history, named as in the details API */}
            {details.rcipet_history?.length > 0 && (
              <FtiReprintHistory reprints={details.rcipet_history} />
            )}

            <FtiDetailsCard title="Customer Information/ข้อมูลลูกค้า">
              <Box sx={getGridSx(4)}>
                <FtiDetailsField label="Customer Name/ชื่อลูกค้า">
                  {details.business_name}
                </FtiDetailsField>
                <FtiDetailsField label="Branch/สาขา">
                  {details.business_branch}
                </FtiDetailsField>
                <FtiDetailsField label="Tax Payer Identification Number/เลขประจำตัวผู้เสียภาษีอากร">
                  {details.abn_number}
                </FtiDetailsField>
                <FtiDetailsField label="Telephone Number/เบอร์โทรศัพท์">
                  {details.phone_formatted}
                </FtiDetailsField>
                <FtiDetailsField
                  label="Customer Address/ที่อยู่ลูกค้า"
                  sx={{ gridColumn: { md: "span 2" } }}
                >
                  {address &&
                    [address, details.business_postcode]
                      .filter(Boolean)
                      .join(" - ")}
                </FtiDetailsField>
              </Box>
            </FtiDetailsCard>

            <FtiAmountBreakdown details={details} formatAmount={formatAmount} />

            <FtiPaymentMedia
              payment={details.payment}
              formatAmount={formatAmount}
            />

            {/* e.g. the note added when this FTI re-issued a cancelled one */}
            {details.remarks && (
              <FtiDetailsCard title="Remarks">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: "16px 24px" }}
                >
                  {details.remarks}
                </Typography>
              </FtiDetailsCard>
            )}
          </Stack>
        )
      )}
      <FtiFormDialog
        open={state.isReissueOpen}
        title={`Cancel & Re-issue /ยกเลิกและออกใหม่ (${
          details?.fti_invoice_number ?? ""
        })`}
        initialValues={reissueInitialValues}
        remarks={`ยกเลิกและออกใบกำกับภาษีฉบับใหม่แทนฉบับเดิมเลขที่ ${
          details?.fti_invoice_number ?? ""
        } เนื่องจากมีการแก้ไขข้อมูลลูกค้า`}
        submitApi={handleReissueFti}
        onSuccess={handleFtiReissued}
        onClose={handleCloseReissue}
      />
      <FtiReprintDialog
        open={state.isReprintOpen}
        remarks={reprintRemarks}
        isGenerating={isGenerating}
        onConfirm={handleConfirmReprint}
        onClose={handleCloseReprint}
      />
      {/* Waits for the details, the export uses the loaded FTI. No cancel, the PDF is already generated */}
      <FtiOutputDialog
        open={Boolean(state.outputFor) && !state.isLoading}
        exportingAction={exportingAction}
        onSelect={handlePrint}
      />
    </ContentLayout>
  );
};
